import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Heading,
  VStack,
  Text,
  useToast,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Container,
  Icon,
  Checkbox,
  useColorModeValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { APP_NAME, APP_TAGLINE } from '../config/branding';
import { FaUserPlus, FaSignInAlt, FaTasks, FaRocket } from 'react-icons/fa';
import { useRegister } from '../hooks/useAuth';
import { useAuth } from '../contexts/AuthContext';
import { getInvite, acceptInvite } from '../api/inviteApi';

const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(5deg); }
  66% { transform: translateY(-10px) rotate(-5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [activeTab, setActiveTab] = useState(inviteToken ? 1 : 0);
  const [inviteInfo, setInviteInfo] = useState<{ email: string; organizationName: string } | null>(null);

  const [regForm, setRegForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    workspaceName: '',
  });

  const [includeSampleData, setIncludeSampleData] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const toast = useToast();
  const registerMutation = useRegister();
  const { user, login, loading: isLoggingIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const bgGradient = useColorModeValue(
    'linear(to-br, blue.400, purple.500, pink.400)',
    'linear(to-br, blue.600, purple.700, pink.600)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const overlayBg = useColorModeValue('whiteAlpha.900', 'blackAlpha.700');

  useEffect(() => {
    if (inviteToken) {
      getInvite(inviteToken)
        .then((info) => {
          setInviteInfo({ email: info.email, organizationName: info.organizationName });
          setRegForm((prev) => ({ ...prev, email: info.email }));
        })
        .catch(() => {
          toast({ title: 'Invalid or expired invite', status: 'error' });
        });
    }
  }, [inviteToken, toast]);

  useEffect(() => {
    if (user) {
      const from = (location.state as any)?.from?.pathname;
      if (from && from !== '/auth') {
        navigate(from, { replace: true });
      } else if (inviteToken) {
        navigate('/dashboard', { replace: true });
      } else if (sessionStorage.getItem('justRegistered') === 'true') {
        sessionStorage.removeItem('justRegistered');
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate, location, inviteToken]);

  const handleRegChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegForm({ ...regForm, [e.target.name]: e.target.value });
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast({
        title: 'Accept the Terms of Service and Privacy Policy to continue',
        status: 'warning',
        duration: 4000,
      });
      return;
    }

    if (inviteToken) {
      try {
        await acceptInvite({
          token: inviteToken,
          firstName: regForm.firstName,
          lastName: regForm.lastName,
          password: regForm.password,
          acceptedTerms: true,
        });
        await login({ email: regForm.email, password: regForm.password });
        toast({ title: 'Welcome to the team!', status: 'success' });
      } catch (err: any) {
        toast({
          title: err.response?.data?.message || 'Registration failed',
          status: 'error',
        });
      }
      return;
    }

    registerMutation.mutate({ ...regForm, includeSampleData, acceptedTerms: true }, {
      onSuccess: async () => {
        sessionStorage.setItem('justRegistered', 'true');
        try {
          await login({ email: regForm.email, password: regForm.password });
        } catch (err: any) {
          toast({ title: err.message || 'Auto-login failed', status: 'error' });
        }
      },
      onError: (err: any) => {
        toast({ title: err.message || 'Registration failed', status: 'error' });
      },
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(loginForm);
      toast({ title: 'Welcome back!', status: 'success' });
    } catch (err: any) {
      toast({ title: err.message || 'Login failed', status: 'error' });
    }
  };

  return (
    <Box minH="100vh" bgGradient={bgGradient} bgSize="400% 400%" animation={`${gradientAnimation} 15s ease infinite`} position="relative" overflow="hidden">
      <Container maxW="6xl" h="100vh" py={8}>
        <Flex align="center" justify="center" h="full">
          <Box w="full" maxW="1200px" bg={overlayBg} borderRadius="2xl" boxShadow="2xl" overflow="hidden" backdropFilter="blur(20px)">
            <Flex direction={{ base: 'column', md: 'row' }} minH="600px">
              <Box flex="1" bgGradient="linear(to-br, blue.500, purple.600)" p={12} display="flex" flexDirection="column" justifyContent="center" alignItems="center" color="white">
                <Icon as={FaTasks} boxSize={16} mb={6} animation={`${floatAnimation} 4s ease-in-out infinite`} />
                <Heading size="2xl" mb={4} textAlign="center">{APP_NAME}</Heading>
                <Text fontSize="xl" textAlign="center" mb={8} opacity={0.9}>{APP_TAGLINE}</Text>
                <VStack spacing={4} align="start">
                  <Flex align="center"><Icon as={FaRocket} mr={3} /><Text>Boost team productivity</Text></Flex>
                  <Flex align="center"><Icon as={FaTasks} mr={3} /><Text>Track progress in real-time</Text></Flex>
                  <Flex align="center"><Icon as={FaUserPlus} mr={3} /><Text>Collaborate seamlessly</Text></Flex>
                </VStack>
              </Box>

              <Box flex="1" p={12} bg={cardBg}>
                {inviteInfo && (
                  <Text mb={4} color="blue.600" fontWeight="semibold">
                    Join {inviteInfo.organizationName}
                  </Text>
                )}
                <Tabs index={activeTab} onChange={setActiveTab} variant="soft-rounded" colorScheme="blue" align="center">
                  <TabList mb={8} bg="gray.100" p={1} borderRadius="xl">
                    <Tab flex={1} fontWeight="semibold"><Icon as={FaSignInAlt} mr={2} />Login</Tab>
                    <Tab flex={1} fontWeight="semibold"><Icon as={FaUserPlus} mr={2} />Register</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel p={0}>
                      <VStack spacing={6} as="form" onSubmit={handleLogin}>
                        <Heading size="lg" color="gray.700">Welcome Back!</Heading>
                        <FormControl isRequired>
                          <FormLabel>Email</FormLabel>
                          <Input type="email" name="email" value={loginForm.email} onChange={handleLoginChange} size="lg" borderRadius="xl" />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel>Password</FormLabel>
                          <Input type="password" name="password" value={loginForm.password} onChange={handleLoginChange} size="lg" borderRadius="xl" />
                        </FormControl>
                        <Button type="submit" colorScheme="blue" size="lg" w="full" borderRadius="xl" isLoading={isLoggingIn} loadingText="Logging...">
                          Sign In
                        </Button>
                      </VStack>
                    </TabPanel>

                    <TabPanel p={0}>
                      <VStack spacing={6} as="form" onSubmit={handleRegister}>
                        <Heading size="lg" color="gray.700">{inviteInfo ? 'Accept Invite' : 'Create Account'}</Heading>
                        <Flex gap={4} w="full">
                          <FormControl isRequired>
                            <FormLabel>First Name</FormLabel>
                            <Input name="firstName" value={regForm.firstName} onChange={handleRegChange} size="lg" borderRadius="xl" />
                          </FormControl>
                          <FormControl isRequired>
                            <FormLabel>Last Name</FormLabel>
                            <Input name="lastName" value={regForm.lastName} onChange={handleRegChange} size="lg" borderRadius="xl" />
                          </FormControl>
                        </Flex>
                        <FormControl isRequired>
                          <FormLabel>Email</FormLabel>
                          <Input type="email" name="email" value={regForm.email} onChange={handleRegChange} isReadOnly={!!inviteInfo} size="lg" borderRadius="xl" />
                        </FormControl>
                        {!inviteInfo && (
                          <FormControl>
                            <FormLabel>Workspace Name</FormLabel>
                            <Input name="workspaceName" value={regForm.workspaceName} onChange={handleRegChange} placeholder="My Agency" size="lg" borderRadius="xl" />
                          </FormControl>
                        )}
                        <FormControl isRequired>
                          <FormLabel>Password</FormLabel>
                          <Input type="password" name="password" value={regForm.password} onChange={handleRegChange} size="lg" borderRadius="xl" />
                        </FormControl>
                        {!inviteInfo && (
                          <Checkbox
                            isChecked={includeSampleData}
                            onChange={(e) => setIncludeSampleData(e.target.checked)}
                            colorScheme="blue"
                          >
                            Add sample client, project & tasks to get started
                          </Checkbox>
                        )}
                        <Checkbox
                          isChecked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          colorScheme="blue"
                          alignItems="flex-start"
                        >
                          <Text fontSize="sm">
                            I agree to the{' '}
                            <Text as={RouterLink} to="/terms" color="blue.500" onClick={(e) => e.stopPropagation()}>
                              Terms of Service
                            </Text>{' '}
                            and{' '}
                            <Text as={RouterLink} to="/privacy" color="blue.500" onClick={(e) => e.stopPropagation()}>
                              Privacy Policy
                            </Text>
                          </Text>
                        </Checkbox>
                        <Button
                          type="submit"
                          colorScheme="blue"
                          size="lg"
                          w="full"
                          borderRadius="xl"
                          isLoading={registerMutation.isPending}
                          loadingText="Creating account..."
                          isDisabled={!acceptedTerms}
                        >
                          {inviteInfo ? 'Join Workspace' : 'Create Account'}
                        </Button>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            </Flex>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default AuthPage;
